const DEFAULT_DECIMAL_PLACES = 2;

/**
 * Rounds a plain (unsigned) decimal string to `decimalPlaces`, half-up, using
 * BigInt string arithmetic — never a float multiply/divide — so it doesn't
 * inherit binary floating-point drift (e.g. 1.005 incorrectly rounding to 1.00).
 * @param value - An unsigned decimal string, e.g. "1.239" or "12".
 * @param decimalPlaces - Target decimal digits.
 * @returns {string} The rounded decimal string.
 */
function roundDecimalString(value: string, decimalPlaces: number): string {
  const [intPartRaw, decPartRaw = ""] = value.split(".");
  const intPart = intPartRaw === "" ? "0" : intPartRaw;
  const keep = decPartRaw.slice(0, decimalPlaces).padEnd(decimalPlaces, "0");
  const nextDigit = decPartRaw[decimalPlaces] ?? "0";
  let scaled = BigInt(intPart) * 10n ** BigInt(decimalPlaces) + BigInt(keep === "" ? "0" : keep);
  if (Number(nextDigit) >= 5) {
    scaled += 1n;
  }
  const scaledString = scaled.toString().padStart(decimalPlaces + 1, "0");
  if (decimalPlaces === 0) {
    return scaledString;
  }
  const newIntPart = scaledString.slice(0, scaledString.length - decimalPlaces);
  const newDecPart = scaledString.slice(scaledString.length - decimalPlaces);
  return `${newIntPart}.${newDecPart}`;
}

/**
 * Decimal-safe rounding for a number, half-up, sidestepping binary
 * floating-point drift (e.g. Math.round(1.005 * 100) / 100 wrongly gives 1).
 * Routes through a high-precision string (toFixed) before rounding on digits.
 * @param value - The number to round.
 * @param decimalPlaces - Target decimal digits.
 * @returns {number} The rounded number.
 */
export function roundToDecimals(value: number, decimalPlaces: number = DEFAULT_DECIMAL_PLACES): number {
  if (Number.isNaN(value)) {
    return value;
  }
  const sign = value < 0 ? -1 : 1;
  const guardDigits = Math.min(decimalPlaces + 6, 20);
  const rounded = roundDecimalString(Math.abs(value).toFixed(guardDigits), decimalPlaces);
  return sign * Number(rounded);
}
